import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';

export type OAuthProvider = 'google' | 'apple' | 'microsoft' | 'facebook';
const PROVIDERS: OAuthProvider[] = ['google', 'apple', 'microsoft', 'facebook'];

export type OAuthRedirectResult = {
  provider: OAuthProvider;
  idToken: string;
} | null;

/**
 * Set right before starting a redirect sign-in so that, after the browser
 * navigates back, we know a completion is expected even if
 * getRedirectResult() comes back empty (iOS Safari / ITP sometimes drops the
 * pending redirect state).
 */
const PENDING_KEY = 'oauth_redirect_pending';

export const markOAuthRedirectPending = (provider: OAuthProvider): void => {
  try {
    sessionStorage.setItem(PENDING_KEY, provider);
  } catch {
    // sessionStorage can be unavailable (private mode); the redirect result
    // path still works, this is only the fallback hint.
  }
};

const readPendingProvider = (): OAuthProvider | null => {
  try {
    const value = sessionStorage.getItem(PENDING_KEY);
    return PROVIDERS.includes(value as OAuthProvider) ? (value as OAuthProvider) : null;
  } catch {
    return null;
  }
};

const clearPendingProvider = (): void => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
};

const providerFromId = (providerId: string | undefined): OAuthProvider | null => {
  if (providerId === 'apple.com') return 'apple';
  if (providerId === 'google.com') return 'google';
  if (providerId === 'microsoft.com') return 'microsoft';
  if (providerId === 'facebook.com') return 'facebook';
  return null;
};

/**
 * Completes a redirect-based OAuth sign-in (see apple.ts / google.ts).
 *
 * On web the popup flow is replaced by a full-page redirect, so after the
 * browser returns to the app we read the pending result here and hand the
 * Firebase ID token back to the caller. Returns null when there is nothing
 * to complete (native platform, or no redirect in progress).
 */
export const completeOAuthRedirect = async (): Promise<OAuthRedirectResult> => {
  if (Capacitor.isNativePlatform()) {
    return null;
  }

  const pendingProvider = readPendingProvider();

  let provider: OAuthProvider | null = null;
  try {
    const result = await FirebaseAuthentication.getRedirectResult();
    if (result?.user) {
      provider =
        providerFromId(result.credential?.providerId) ||
        providerFromId(result.additionalUserInfo?.providerId);
    }
  } catch (error) {
    // A sign-in we started failed on the provider side (e.g. Apple not enabled
    // for web yet) — hand it to the caller so the user sees why.
    if (pendingProvider) {
      clearPendingProvider();
      throw Object.assign(error as object, { provider: pendingProvider });
    }
    console.warn('Failed to read OAuth redirect result:', error);
  }

  // Fallback: the redirect completed (a sign-in was in flight) but
  // getRedirectResult() returned nothing — check for a signed-in user.
  if (!provider && pendingProvider) {
    try {
      const { user } = await FirebaseAuthentication.getCurrentUser();
      if (user) {
        provider = pendingProvider;
      }
    } catch (error) {
      console.warn('Failed to read current Firebase user:', error);
    }
  }

  if (!provider) {
    // Only clear the hint once we are no longer mid-redirect; on the first
    // load back it may take a tick for Firebase to settle.
    if (pendingProvider) clearPendingProvider();
    return null;
  }

  clearPendingProvider();

  const { token } = await FirebaseAuthentication.getIdToken();
  if (!token) {
    return null;
  }

  return { provider, idToken: token };
};
